import './ChatWithBotWidget.scss';
import React, { useEffect, useCallback, useState } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import { ConversationHistory } from '../../conversations/frontend/ConversationHistory';
import { ChatMessageInputBox } from './ChatMessageInputBox';
import { useAPI } from '../../frontend/components/APIProvider';
import { SpinnerIconButton } from '../../frontend/elements/SpinnerIconButton';
import { api as apiDirect } from '../../frontend/components/api';
import Alert from '@mui/material/Alert';
import WebChatEventConnectionManager from './WebChatEventConnectionManager';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import LocalTranslatedText from '@/translation/frontend/components/LocalTranslatedText';
import { randomString } from '@utils/random';

export function ChatWithBotWidget({
  conversation,
  webChatConfiguration,
  showResetButton,
  showStatusInToolbar = true,
  onResetClicked,
  showInternalEvents,
  showBoundingBox,
  onNewEvent,
}) {
  const [localWebChatEventConnectionManager] = useState(
    new WebChatEventConnectionManager()
  );

  if (showInternalEvents === undefined) {
    showInternalEvents = false;
  }

  let api = useAPI();
  if (!api) {
    api = apiDirect;
  }

  const [events, setEvents] = React.useState([]);
  const [error, setError] = React.useState(null);
  const cardContentRef = React.useRef();

  function scrollToBottom() {
    if (cardContentRef?.current) {
      cardContentRef.current.scrollTop = cardContentRef.current.scrollHeight;
    }
  }

  const addEvent = useCallback(
    (newEventData) => {
      // Ignore the event if it is a user event, since we will track all user created
      // events locally
      if (newEventData.role === 'user') {
        return;
      }

      setEvents((events) => {
        // Double check to see if the new event data being added isn't already in the list of events.
        // If so, update it with the latest data. Otherwise append it to the end of the list.
        for (let i = 0; i < events.length; i++) {
          if (events[i]._id === newEventData._id) {
            return [
              ...events.slice(0, i),
              newEventData,
              ...events.slice(i + 1),
            ];
          }
        }

        if (onNewEvent) {
          onNewEvent(newEventData);
        }

        setTimeout(() => {
          scrollToBottom();
        }, 100);

        return [...events, newEventData];
      });
    },
    [onNewEvent]
  );

  useEffect(() => {
    localWebChatEventConnectionManager.updateEventHandle(addEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addEvent]);

  const startListeningForMessages = useCallback(
    (conversationId, conversationToken, mostRecentEventTime) => {
      localWebChatEventConnectionManager.subscribeToEvents(
        conversationId,
        conversationToken,
        mostRecentEventTime,
        addEvent
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addEvent]
  );

  useEffect(() => {
    if (conversation?._id) {
      setEvents([]);
      api
        .getRecentEvents(
          conversation._id,
          conversation?.metadata?.conversation_token
        )
        .then((data) => {
          const reversedEvents = data.messages;
          reversedEvents.reverse();
          setEvents(reversedEvents);

          const mostRecentMessage = data.messages[data.messages.length - 1];
          startListeningForMessages(
            conversation._id,
            conversation?.metadata?.conversation_token,
            mostRecentMessage?.created_at ??
              new Date(
                new Date().getTime() - 1000 * 3600 * 24 * 7
              ).toISOString()
          );
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        });
    }
  }, [
    startListeningForMessages,
    conversation?._id,
    conversation?.metadata?.conversation_token,
    api,
  ]);

  React.useEffect(() => {
    return () => {
      localWebChatEventConnectionManager.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendMessage = useCallback(
    (message, contentBlocks) => {
      const newMessageLocalId = `new-${randomString(8)}`;
      const newEvent = {
        _id: newMessageLocalId,
        role: 'user',
        content: message,
        created_at: new Date().toISOString(),
      };

      setEvents((events) => {
        return [...events, newEvent];
      });
      setTimeout(() => {
        scrollToBottom();
      }, 100);

      api
        .newMessage(
          conversation._id,
          message,
          contentBlocks,
          conversation?.metadata?.conversation_token
        )
        .then(
          (response) => {
            const newEvent = response;
            setEvents((events) => {
              return events.map((message) => {
                if (message._id === newMessageLocalId) {
                  return newEvent;
                } else {
                  return message;
                }
              });
            });
          },
          (error) => {
            setError(error);
          }
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, conversation, api]
  );

  const handleResetClicked = useCallback(() => {
    if (onResetClicked) {
      return onResetClicked();
    }
  }, [onResetClicked]);

  const handleConversationDownloadClicked = useCallback(() => {
    return api
      .downloadConversationExportPublic(
        conversation._id,
        conversation?.metadata?.conversation_token
      )
      .then((pdfData) => {
        const blob = new Blob([pdfData], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `doc.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      });
  }, [conversation, api]);

  const swallowScrollEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const swallowClickEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Auto-fit mode requires BOTH the mode setting AND an actual background image.
  // Without an image, auto-fit is meaningless and would only strip padding (bug #005).
  const autoBackgroundUrl = webChatConfiguration?.background_watermark_url;
  const isAutoFit =
    webChatConfiguration?.background_image_mode === 'auto-fit' &&
    !!autoBackgroundUrl;
  const [autoFitSize, setAutoFitSize] = React.useState(null);
  const containerOuterRef = React.useRef(null);

  React.useEffect(() => {
    if (!isAutoFit || !autoBackgroundUrl) {
      setAutoFitSize(null);
      return;
    }

    let cancelled = false;
    let imgW = 0,
      imgH = 0;

    const recalculate = () => {
      if (cancelled || !containerOuterRef.current) return;
      if (imgW > 0 && imgH > 0) {
        const rect = containerOuterRef.current.getBoundingClientRect();
        // Allow a small padding inside the container
        const maxW = rect.width;
        const maxH = rect.height;
        const scale = Math.min(maxW / imgW, maxH / imgH, 1);
        setAutoFitSize({
          width: Math.floor(imgW * scale),
          height: Math.floor(imgH * scale),
        });
      }
    };

    const img = new window.Image();
    img.onload = () => {
      imgW = img.naturalWidth;
      imgH = img.naturalHeight;
      recalculate();
    };
    img.onerror = () => {
      if (!cancelled) setAutoFitSize(null);
    };
    img.src = autoBackgroundUrl;

    const observer = new window.ResizeObserver(() => recalculate());
    if (containerOuterRef.current) observer.observe(containerOuterRef.current);

    window.addEventListener('resize', recalculate);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', recalculate);
      observer.disconnect();
    };
  }, [isAutoFit, autoBackgroundUrl]);

  const background_color_style = {
    backgroundColor: webChatConfiguration?.widget_background_color,
  };
  const background_watermark_url =
    webChatConfiguration?.background_watermark_url;
  let background_watermark_style = {};
  if (background_watermark_url) {
    const mode = webChatConfiguration?.background_image_mode ?? 'watermark';
    const opacity = webChatConfiguration?.background_image_opacity ?? 0.1;

    const modeStyles = {
      watermark: { backgroundSize: 'contain', backgroundRepeat: 'repeat' },
      cover: {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },
      contain: {
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },
      'auto-fit': {
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },
    };

    background_watermark_style = {
      backgroundImage: `url(${background_watermark_url})`,
      ...(modeStyles[mode] ?? modeStyles.watermark),
      opacity: opacity,
    };
  }

  const autoFitContainerStyle = autoFitSize
    ? { display: 'flex', justifyContent: 'center', alignItems: 'stretch' }
    : { display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const autoFitCardStyle = autoFitSize
    ? {
        width: autoFitSize.width,
        height: '100%',
        maxHeight: '100%',
        flexGrow: 0,
        flexShrink: 0,
        alignSelf: 'stretch',
      }
    : {};

  const chatStatusLabel =
    conversation?.status === 'completed' ? 'Closed' : 'Active';

  const chatStatusClass =
    conversation?.status === 'completed' ? 'is-closed' : 'is-active';

  return (
    <div
      className={`prospera-chat-with-bot-widget${isAutoFit ? ' auto-fit-active' : ''}`}
      onScroll={swallowScrollEvent}
      onClick={swallowClickEvent}
      ref={containerOuterRef}
      style={autoFitContainerStyle}
    >
      <Card
        className="conversation-events-card"
        sx={background_color_style}
        elevation={showBoundingBox ? 1 : 0}
        style={autoFitCardStyle}
      >
        {background_watermark_url ? (
          <div
            className={'conversation-background-watermark'}
            style={background_watermark_style}
          />
        ) : null}
        <CardContent
          ref={cardContentRef}
          style={isAutoFit ? { paddingLeft: 0, paddingRight: 0 } : undefined}
        >
          {showResetButton ? (
            <div className={'chat-session-toolbar'}>
              {showStatusInToolbar ? (
                <div className={'chat-session-status'}>
                  <Typography variant="caption" className="chat-session-label">
                    <LocalTranslatedText text="Chat status" />
                  </Typography>
                  <Chip
                    size="small"
                    label={chatStatusLabel}
                    className={`chat-status-chip ${chatStatusClass}`}
                  />
                </div>
              ) : (
                <div className={'chat-session-status-placeholder'} />
              )}

              <div className={'prospera-conversation-action-buttons'}>
                <div className="chat-action-button-item">
                  <SpinnerIconButton
                    className="chat-action-icon"
                    onClick={handleResetClicked}
                    title={'Start a New Conversation'}
                    sx={
                      webChatConfiguration?.chat_action_button_color
                        ? {
                            color:
                              webChatConfiguration.chat_action_button_color,
                          }
                        : undefined
                    }
                  >
                    <RestartAltIcon />
                  </SpinnerIconButton>
                  <Typography variant="caption" className="chat-action-label">
                    <LocalTranslatedText text="New chat" />
                  </Typography>
                </div>
                <div className="chat-action-button-item">
                  <SpinnerIconButton
                    className="chat-action-icon"
                    onClick={handleConversationDownloadClicked}
                    title={'Download a Conversation Export'}
                    sx={
                      webChatConfiguration?.chat_action_button_color
                        ? {
                            color:
                              webChatConfiguration.chat_action_button_color,
                          }
                        : undefined
                    }
                  >
                    <DownloadIcon />
                  </SpinnerIconButton>
                  <Typography variant="caption" className="chat-action-label">
                    <LocalTranslatedText text="Download" />
                  </Typography>
                </div>
              </div>
            </div>
          ) : null}

          <div className={'conversation-history-box'}>
            <ConversationHistory
              events={events}
              onlyShowMessages={!showInternalEvents}
              conversation={conversation}
              webChatConfiguration={webChatConfiguration}
            />
            {events.length === 0 ? (
              <div className={'prospera-empty-conversation-message'}>
                <LocalTranslatedText
                  text={
                    'Initiate the conversation by sending a message to the bot with the input field at the bottom.'
                  }
                />
              </div>
            ) : null}
          </div>
        </CardContent>

        <CardActions
          className={'card-actions-input-area'}
          style={isAutoFit ? { padding: 0 } : undefined}
        >
          <ChatMessageInputBox
            onSendMessage={handleSendMessage}
            webChatConfiguration={webChatConfiguration}
          />
        </CardActions>

        {error ? (
          <Alert severity="error">
            <LocalTranslatedText text={error.message} />
          </Alert>
        ) : null}
      </Card>
    </div>
  );
}
